import { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid, GridColDef, GridOverlay } from "@mui/x-data-grid";
import { Chip, InputAdornment, TextField, ThemeProvider } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { IVulnerability } from "system/types/interfaces";
import { toFormatedDate } from "system/libraries/utilities";
import { muiTheme } from "system/styles/muiTheme";
import useApi from "system/hooks/use-api";
import LoadingPage from "./system/LoadingPage";

const VulnerabilitiesPage = () => {
  const api = useApi();
  const [vulnerabilities, setVulnerabilities] = useState<IVulnerability[]>([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true)

  const getSeverityLabel = (threatScore: number) => {
    if (threatScore >= 9.0) return "Critical";
    if (threatScore >= 7.0) return "High";
    if (threatScore >= 4.0) return "Medium";
    if (threatScore >= 0.1) return "Low";
    return "None";
  };

  const getSeverityColor = (threatScore: number) => {
    if (threatScore >= 9.0) return "#FF3131"; // red
    if (threatScore >= 7.0) return "#FFA031"; // orange
    if (threatScore >= 4.0) return "#FFD131"; // yellow
    if (threatScore >= 0.1) return "#2F97C9"; // blue
    return "#2CE22C"; // green
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.vulnerabilities.getVulnerabilities();
      const items = response.items ?? []

      const mappedData: IVulnerability[] = items.map((item: any) => ({
        id: item.id,
        product: item.product,
        iconUrl: item.iconUrl,
        vulnerabilityId: item.vulnerabilityId,
        availableRemediation: item.availableRemediation,
        threatScore: item.threatScore,
        affectedEndpoints: item.affectedEndpoints,
        remediatedEndpoints: item.remediatedEndpoints,
        patchAvailableDays: item.remediationAvailableDays,
        patchAvailableDate: item.remediationAvailableDate,
        percentRemediated:
          item.affectedEndpoints > 0
            ? Math.round((item.remediatedEndpoints / item.affectedEndpoints) * 100)
            : 0,
        severity: getSeverityLabel(item.threatScore),
      }));

      //Custom sorting logic
      const sortedData = mappedData.sort((a, b) => {
        //Severity (higher threatScore first)
        if (b.threatScore !== a.threatScore) {
          return b.threatScore - a.threatScore;
        }
        //Number of affected endpoints (more affected first)
        if (b.affectedEndpoints !== a.affectedEndpoints) {
          return b.affectedEndpoints - a.affectedEndpoints;
        }
        //Age of vulnerability (older CVE first — alphabetically)
        return a.vulnerabilityId.localeCompare(b.vulnerabilityId);
      });

      setVulnerabilities(sortedData);
    } catch (error) {
      console.error("Failed to load vulnerabilities:", error);
    } finally {
    setLoading(false);
  }
  }, [api]);


  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  }, []);

  const filteredVulnerabilities = useMemo(() => {
    if (!vulnerabilities) return [];
    return vulnerabilities.filter((v) =>
      Object.values(v).some((value) =>
        String(value).toLowerCase().includes(searchText.toLowerCase())
      )
    );
  }, [searchText, vulnerabilities]);

  const severityOrder: Record<string, number> = {
    None: 0,
    Low: 1,
    Medium: 2,
    High: 3,
    Critical: 4,
  };

  const columns: GridColDef[] = [
    // {
    //   field: "product",
    //   headerName: "Product",
    //   width: 250,
    //   renderCell: (params) => {
    //     const defaultIconUrl = "/img/DefaultPackage.png"; // your default icon path
    //     const iconUrl =
    //       params.row.iconUrl && params.row.iconUrl.trim() !== ""
    //         ? params.row.iconUrl
    //         : defaultIconUrl;

    //     return (
    //       <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    //         <img
    //           src={iconUrl}
    //           alt={params.row.product || "Package Icon"}
    //           style={{ width: 24, height: 24, borderRadius: 4, objectFit: "contain" }}
    //         />
    //         <span>{params.row.product}</span>
    //       </div>
    //     );
    //   },
    // },
    {
      field: "product",
      headerName: "Product",
      width: 250,
      renderCell: (params) => {
        return (
          <span>{params.row.product}</span>
        );
      },
    },
    { field: "vulnerabilityId", headerName: "Vulnerability ID", width: 200 },
    { field: "availableRemediation", headerName: "Available Remediation", width: 200 },
    {
      field: "severity",
      headerName: "Severity",
      width: 150,
      sortable: true,
      sortComparator: (v1, v2) =>
        (severityOrder[v1 as string] ?? 0) - (severityOrder[v2 as string] ?? 0),
      renderCell: (params) => (
        <Chip
          label={params.row.severity}
          variant="outlined"
          style={{
            backgroundColor: getSeverityColor(params.row.threatScore),
            color: "black",
            fontWeight: 500,
            cursor: "pointer",
            width: 75,
          }}
        />
      ),
    },
    {
      field: "threatScore",
      headerName: "Threat Score",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <span>{params.value === 0 ? "-" : params.value}</span>
      ),
    },
    {
      field: "affectedEndpoints",
      headerName: "Vulnerable Endpoints",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => <span>{params.value === 0 ? "-" : params.value}</span>,
    },
    {
      field: "remediatedEndpoints",
      headerName: "Remediated Endpoints",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => <span>{params.value === 0 ? "-" : params.value}</span>,
    },
    {
      field: "percentRemediated",
      headerName: "% Remediated",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        params.value === 0 ? "-" : `${params.value}%`,
    },
  ];

  return (
  <ThemeProvider theme={muiTheme}>
    {loading ? (   // Show Aiden loading screen until data loads
      <LoadingPage />
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <h3 style={{ color: "var(--text-color)", fontWeight: 400, fontSize: 28 }}>
            Vulnerabilities
          </h3>
          <TextField
            label="Search"
            variant="filled"
            size="small"
            value={searchText}
            onChange={handleSearch}
            sx={{ width: 300 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </div>

        <div style={{ height: 640, width: "100%" }}>
          <DataGrid
            rows={filteredVulnerabilities}
            columns={columns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 20, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #424242",
                backgroundColor: "#3c3c3c",
                color: "#ffffff",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#1e1e1e",
                borderBottom: "1px solid #424242",
                color: "white",
              },
              "& .MuiDataGrid-footerContainer": {
                backgroundColor: "#1e1e1e",
                borderTop: "1px solid #424242",
                color: "white",
                justifyContent: "center",
              },
              "& .MuiTablePagination-toolbar": {
                color: "white",
                alignItems: "baseline",
                paddingTop: 10,
              },
            }}
          />
        </div>
      </div>
    )}
  </ThemeProvider>
);
};

export default VulnerabilitiesPage;